import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import type { ApiWrapped } from '@core/http/api-response.types';
import { apiUrl } from '@core/http/api-url.util';

/**
 * Server-backed account snapshot for UI (dashboard summary).
 *
 * **Credits** (`used` / `limit`) come from the API: never persist them in localStorage as authoritative state.
 * After sign-in, call `refreshCredits()` to repopulate.
 */
type DashboardPayload = {
  credits: { used: number; limit: number };
};

@Injectable({ providedIn: 'root' })
export class UserAccountService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  /** Null until first successful fetch or after a hard error. */
  private readonly creditsState = signal<{ used: number; limit: number } | null>(null);

  readonly credits = this.creditsState.asReadonly();

  readonly creditsRemaining = computed(() => {
    const c = this.creditsState();
    if (!c) return null;
    return Math.max(0, c.limit - c.used);
  });

  /** Loads `credits` from `GET /user/dashboard` (requires Bearer). */
  refreshCredits(): Observable<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(undefined);
    }
    return this.http.get<ApiWrapped<DashboardPayload>>(apiUrl('/user/dashboard')).pipe(
      tap((res) => {
        const c = res?.data?.credits;
        if (c && typeof c.used === 'number' && typeof c.limit === 'number') {
          this.creditsState.set({ used: c.used, limit: c.limit });
        }
      }),
      map(() => undefined),
      catchError(() => {
        this.creditsState.set(null);
        return of(undefined);
      }),
    );
  }

  clearCreditsCache(): void {
    this.creditsState.set(null);
  }
}
